import { AlertCircle, Info, Plus, RefreshCw } from "lucide-react";
import { useCallback, useMemo, useState } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { ColocaPageHeader } from "@/shared/components/layout/ColocaPageHeader";
import { useAppShellBreadcrumb } from "@/shared/components/layout/useAppShellBreadcrumb";
import { NoAccessView } from "@/shared/components/NoAccessView";
import { useIsMobile } from "@/shared/hooks/use-mobile";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { colocaToast } from "@/shared/utils/toast";

import { CorridorRouteCards } from "../components/routes/CorridorRouteCards";
import { CorridorRoutesTable } from "../components/routes/CorridorRoutesTable";
import { CreateRouteDialog, type CreateRouteIntent } from "../components/routes/CreateRouteDialog";
import { DeleteRouteDialog } from "../components/routes/DeleteRouteDialog";
import { RouteDetailDrawer } from "../components/routes/RouteDetailDrawer";
import { RouteKpiStrip } from "../components/routes/RouteKpiStrip";
import { SwitchProviderDialog } from "../components/routes/SwitchProviderDialog";
import { corridorTripleKey } from "../constants/paymentRoutes.constants";
import { useProviderRoutes } from "../hooks/useProviderRoutes";
import { PaymentDirection } from "../types/paymentFees.types";
import type { CorridorRouteCell } from "../types/paymentRoutes.types";

/**
 * `/app/payments-config/routes` — which corridors the payments runtime can operate, for INTERNAL staff.
 *
 * Both write endpoints are `userTypes: [INTERNAL]`, and the route already gates on `userTypes: [INTERNAL]`
 * via `RequireAccess` in `DashboardNavigation`. The RBAC half (`payments:read` / `payments:write`) is decided
 * by the backend: a 401/403 on the list latches into the no-access view, and legacy `scopes[]` are NOT
 * consulted.
 *
 * Freshness: no polling. Routes are configuration — they change by human action only, and there is no
 * websocket for payments. Data reloads on the manual button and after every successful create, switch or
 * delete.
 */
const ProviderRoutesPage = () => {
  const { t } = useTranslations("paymentRoutes");
  const { t: tGlobal } = useTranslations("global");
  const isMobile = useIsMobile();

  useAppShellBreadcrumb(
    useMemo(
      () => [
        { label: tGlobal("sidebar_section_gestion") },
        { label: tGlobal("sidebar_menu_payment_routes"), to: "/app/payments-config/routes" },
      ],
      [tGlobal],
    ),
  );

  const state = useProviderRoutes();

  const [createIntent, setCreateIntent] = useState<CreateRouteIntent | null>(null);
  const [switchTarget, setSwitchTarget] = useState<CorridorRouteCell | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CorridorRouteCell | null>(null);

  const hasOffCatalog = useMemo(() => state.cells.some((cell) => cell.offCatalog), [state.cells]);

  const openCreateFor = useCallback((cell: CorridorRouteCell) => {
    setCreateIntent({
      corridorKey: corridorTripleKey(cell),
      direction: cell.direction as PaymentDirection,
    });
  }, []);

  const handleCreated = useCallback(
    (_route: unknown, corridorLabel: string) => {
      setCreateIntent(null);
      colocaToast.success(t("toast_created", { corridor: corridorLabel }));
      state.refresh();
    },
    [state, t],
  );

  const handleDeleted = useCallback(
    (wasMissing: boolean, corridorLabel: string) => {
      setDeleteTarget(null);
      // The detail panel of a corridor that no longer has a route would show a stale route.
      state.select(null);
      if (wasMissing) colocaToast.info(t("error_delete_missing"));
      else colocaToast.success(t("toast_deleted", { corridor: corridorLabel }));
      state.refresh();
    },
    [state, t],
  );

  const handleSwitched = useCallback(
    (corridorLabel: string) => {
      setSwitchTarget(null);
      state.select(null);
      colocaToast.success(t("toast_switched", { corridor: corridorLabel }));
      state.refresh();
    },
    [state, t],
  );

  // A 401/403 on the route list is terminal for this surface.
  if (!state.hasAccess) return <NoAccessView />;

  return (
    <div className="space-y-6 p-2">
      <ColocaPageHeader
        title={t("page_title")}
        description={t("page_subtitle")}
        actions={
          <>
            <ColocaButton
              variant="default"
              size="sm"
              icon={RefreshCw}
              aria-label={t("refresh_aria")}
              className={cn(state.isRefreshing && "[&_svg]:animate-spin")}
              onClick={state.refresh}
            >
              {t("refresh_button")}
            </ColocaButton>
            <ColocaButton variant="primary" icon={Plus} onClick={() => setCreateIntent({})}>
              {t("button_new_route")}
            </ColocaButton>
          </>
        }
      />

      {/* Truncation must be disclosed: coverage is computed from the loaded rows. */}
      {state.truncated && (
        <ColocaAlert
          variant="warning"
          icon={AlertCircle}
          text={t("truncated_notice", { loaded: state.loadedCount, total: state.totalCount })}
        />
      )}

      {state.hasError ? (
        <ColocaAlert
          variant="destructive"
          icon={AlertCircle}
          heading={t("error_load")}
          text={t("error_load_detail")}
          actions={
            <ColocaButton variant="default" size="sm" icon={RefreshCw} onClick={state.refresh}>
              {t("error_retry")}
            </ColocaButton>
          }
        />
      ) : (
        <>
          {state.isLoading ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
              {[0, 1, 2, 3].map((index) => (
                <ColocaSkeleton key={index} className="h-[5.5rem] rounded-xl" />
              ))}
            </div>
          ) : (
            <RouteKpiStrip kpis={state.kpis} />
          )}

          {isMobile ? (
            <CorridorRouteCards
              cells={state.cells}
              isLoading={state.isLoading}
              onView={(cell) => state.select(cell.key)}
              onCreate={openCreateFor}
            />
          ) : (
            <CorridorRoutesTable
              cells={state.cells}
              isLoading={state.isLoading}
              onView={(cell) => state.select(cell.key)}
              onCreate={openCreateFor}
            />
          )}

          {hasOffCatalog && <ColocaAlert variant="secondary" icon={Info} text={t("offcatalog_notice")} />}

          {/* Why this matters — an unrouted corridor is not a cosmetic gap, it is a dead corridor. */}
          <ColocaAlert variant="secondary" icon={Info} text={t("why_banner")} />
        </>
      )}

      {createIntent && (
        <CreateRouteDialog
          intent={createIntent}
          open
          onClose={() => setCreateIntent(null)}
          onCreated={handleCreated}
          cells={state.cells}
          routes={state.routes}
          onSwitchRequest={(cell) => {
            setCreateIntent(null);
            setSwitchTarget(cell);
          }}
        />
      )}

      {state.selectedCell?.route && (
        <RouteDetailDrawer
          cell={state.selectedCell}
          open
          onClose={() => state.select(null)}
          onSwitch={setSwitchTarget}
          onDelete={setDeleteTarget}
        />
      )}

      {switchTarget?.route && (
        <SwitchProviderDialog
          cell={switchTarget}
          open
          onClose={() => setSwitchTarget(null)}
          routes={state.routes}
          onSwitched={handleSwitched}
          onFinished={state.refresh}
        />
      )}

      {deleteTarget?.route && (
        <DeleteRouteDialog
          cell={deleteTarget}
          open
          onClose={() => setDeleteTarget(null)}
          onDeleted={handleDeleted}
        />
      )}
    </div>
  );
};

export default ProviderRoutesPage;
